import { useEffect, useState } from "react";
import type { DashboardData } from "../types";
import { api, ApiError } from "../api";
import { formatCurrency } from "../format";
import ErrorState from "./ErrorState";
import { BarChart, PieChart, HBars } from "./charts";
import {
  IconBoxes,
  IconWallet,
  IconAlert,
  IconPause,
  IconPercent,
  IconScale,
  IconClock,
  IconPicking,
} from "../icons";

const COLORS = ["#0c7a3f", "#15a85a", "#34d27e", "#0a5c30", "#5cd99a", "#1f7a4d", "#8be0b3", "#0e9488"];

export default function Dashboard({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .dashboard()
      .then((d) => alive && setData(d))
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof ApiError ? err : new ApiError(String(err)));
      });
    return () => {
      alive = false;
    };
  }, [refreshKey, reload]);

  if (error)
    return (
      <ErrorState
        message={error.message}
        code={error.code}
        onRetry={() => setReload((n) => n + 1)}
      />
    );
  if (!data) return <div className="empty">Carregando indicadores...</div>;

  const c = data.cards;
  // 8 cartões — fecham linhas completas (4×2) sem deixar espaços vazios.
  const cards = [
    { label: "Valor em estoque", value: formatCurrency(c.valor_estoque), hint: `${c.total_itens} itens cadastrados`, Icon: IconWallet, tone: "green" },
    { label: "Total de itens", value: c.total_itens, hint: `${c.itens_ativos} ativos`, Icon: IconBoxes, tone: "emerald" },
    { label: "Acurácia do estoque", value: `${c.acuracia}%`, hint: "contagens conformes", Icon: IconPercent, tone: "teal" },
    { label: "Lead time de separação", value: `${c.lead_time_medio} dias`, hint: `${c.separacoes_pendentes} pendente(s)`, Icon: IconClock, tone: "lime" },
    { label: "Itens abaixo do mínimo", value: c.itens_abaixo_minimo, hint: `${c.itens_zerados} zerado(s)`, Icon: IconAlert, tone: "amber" },
    { label: "Estoque parado/obsoleto", value: c.itens_obsoletos, hint: formatCurrency(c.valor_obsoleto), Icon: IconPause, tone: "amber" },
    { label: "Divergências de inventário", value: c.divergencias, hint: "físico × sistema", Icon: IconScale, tone: "red" },
    { label: "Separações pendentes", value: c.separacoes_pendentes, hint: "aguardando atendimento", Icon: IconPicking, tone: "teal" },
  ];

  return (
    <div className="dashboard">
      <div className="cards">
        {cards.map((card) => (
          <div className={`card tone-${card.tone}`} key={card.label}>
            <div className="card-icon">
              <card.Icon size={22} />
            </div>
            <div className="card-body">
              <span className="card-value">{card.value}</span>
              <span className="card-label">{card.label}</span>
              {card.hint && <span className="card-hint">{card.hint}</span>}
            </div>
          </div>
        ))}
      </div>

      <div className="charts">
        <ChartBox title="Valor em estoque por categoria">
          <PieChart
            data={data.valorPorCategoria}
            valueKey="total"
            labelKey="categoria"
            colors={COLORS}
          />
        </ChartBox>

        <ChartBox title="Itens por situação de estoque">
          <PieChart
            data={data.estoquePorSituacao}
            valueKey="total"
            labelKey="situacao"
            colors={["#0c7a3f", "#c98a14", "#e0913a", "#dc2626"]}
          />
        </ChartBox>

        <ChartBox title="Movimentações por mês (6 meses)">
          <BarChart
            data={data.movimentacoesPorMes}
            xKey="mes"
            series={[
              { key: "entradas", label: "Entradas", color: "#0c7a3f" },
              { key: "saidas", label: "Saídas", color: "#34d27e" },
            ]}
          />
        </ChartBox>

        <ChartBox title="Top itens por valor em estoque">
          <HBars
            data={data.topItens}
            labelKey="descricao"
            valueKey="total"
            color="#0c7a3f"
            format={formatCurrency}
          />
        </ChartBox>
      </div>
    </div>
  );
}

function ChartBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="chart-box">
      <h3>{title}</h3>
      {children}
    </div>
  );
}
